import * as React from "react";

import { DataTable, Page, Spinner } from "src/components/common";
import { Form, Select, Tooltip } from "antd";
import { Button } from "src/components/buttons";

import OrganizationAPI from "../API/OrganizationAPI";
import DatasetAPI from "./DatasetAPI";
import PickListContext from "src/context/pickListContext";
import AuthorizationWrapper from "src/common/AuthorizationWrapper";
import { AdminPermissions } from "src/common/Permissions";
import { ActionIcon } from "src/components/forms/ActionColumnIconsAndButton";
import { Icon } from "src/components/icons/Icon";
import { User, UserLock } from "lucide-react";
import { toast } from "react-toastify";

interface IDatasetUsersProps {
	match?: any;
}

interface IDatasetUsersState {
	organizationId: number;
	datasetId: number;
	datasetUsers: DatasetUserDTO[];
	organizationUsers: CurrentUserDTO[];
	currentUser: DatasetUserDTO;
	filtered: any[];
	loading: boolean;
	isLoading: boolean;
}

class OrganizationDatasetsUsers extends React.Component<
	IDatasetUsersProps,
	IDatasetUsersState
> {
	public static contextType = PickListContext;
	private fullOrganizationUserList: CurrentUserDTO[];

	private roleList: RoleDTO[] = [];

	public constructor(props: any) {
		super(props);
		this.state = {
			datasetUsers: [],
			organizationUsers: [],
			currentUser: { datasetId: this.props.match.params.datasetId },
			organizationId: this.props.match.params.organizationId,
			datasetId: this.props.match.params.datasetId,
			filtered: [],
			loading: false,
			isLoading: true,
		};
	}

	public componentDidMount() {
		this.loadData();
	}

	public render() {
		const columns = this.getColumns();
		const validation = this.validateCurrentUser(this.state.currentUser);

		return this.state.isLoading !== false ? (
			<Spinner />
		) : (
			<Page title="Dataset Users">
				<div className="container-fluid">
					<DataTable
						data={this.state.loading ? [] : this.state.datasetUsers}
						columns={columns}
						filtered={this.state.filtered}
						onFilteredChange={(filtered: any) => this.setState({ filtered })}
						filterable={true}
						loading={this.state.loading}
						showPagination={true}
						className="mb-5"
					/>
					<AuthorizationWrapper
						type="hide"
						permissions={[AdminPermissions.EditOrganizationUser]}
					>
						<div className="row">
							<div className="col-5 form-group">
								<label>Users</label>
								<Form.Item
									validateStatus={validation.user.isValid ? "success" : ""}
								>
									<AuthorizationWrapper
										type="disable"
										permissions={[AdminPermissions.EditOrganizationUser]}
									>
										<Select
											placeholder="Name"
											onChange={this.onUserChange}
											value={this.state.currentUser.userId}
										>
											{this.state.organizationUsers.map((user) => {
												const displayName =
													user.firstName + " " + user.lastName;
												return (
													<Select.Option key={`${user.id}`} value={user.id}>
														{displayName}
													</Select.Option>
												);
											})}
										</Select>
									</AuthorizationWrapper>
								</Form.Item>
							</div>
							<div className="col-3 form-group">
								<label>Role</label>
								<Form.Item
									validateStatus={validation.role.isValid ? "success" : ""}
								>
									<AuthorizationWrapper
										type="disable"
										permissions={[AdminPermissions.EditOrganizationUser]}
									>
										<Select
											id="roleSelection"
											onChange={this.onRoleChange}
											value={this.state.currentUser.roleId}
										>
											{this.roleList.map((item) => (
												<Select.Option key={`${item.id}`} value={item.id}>
													{item.name}
												</Select.Option>
											))}
										</Select>
									</AuthorizationWrapper>
								</Form.Item>
							</div>
							<div className="col-1 form-group d-flex align-items-end">
								<AuthorizationWrapper
									type="disable"
									permissions={[AdminPermissions.EditOrganizationUser]}
								>
									<Button
										className="btn-outline-primary"
										onClick={this.onSubmit}
										loading={this.state.loading}
										disabled={!this.isValid(validation)}
									>
										Add
									</Button>
								</AuthorizationWrapper>
							</div>
						</div>
					</AuthorizationWrapper>
				</div>
			</Page>
		);
	}
	private getColumns() {
		const columns = [
			{
				Header: <Tooltip title="Users">Users</Tooltip>,
				accessor: "userName",
				filterable: true,
				Cell: (row: any) => this.returnUserComponent(row),
			},
			{
				Header: <Tooltip title="Role">Role</Tooltip>,
				accessor: "roleId",
				Cell: (rowInfo: any) => (
					<React.Fragment>
						{this.getRoleName(rowInfo.original.roleId)}
					</React.Fragment>
				),
			},
			{
				Header: <Tooltip title="Actions">Actions</Tooltip>,
				accessor: "Actions",
				Cell: (rowInfo: any) => (
					<AuthorizationWrapper
						type="disable"
						permissions={[AdminPermissions.EditOrganizationUser]}
					>
						<ActionIcon
							onClick={() => {
								this.deleteUser(
									rowInfo.original.userId,
									rowInfo.original.roleId
								);
							}}
							title={`Remove user ${rowInfo.original.userName}`}
							type="delete"
						/>
					</AuthorizationWrapper>
				),
				Filter: (
					<Button
						className="btn-outline-primary"
						onClick={() =>
							this.setState({ filtered: [{ id: "isActive", value: "Active" }] })
						}
					>
						Clear
					</Button>
				),
				id: "Actions",
			},
		];
		return columns;
	}

	private returnUserComponent(row: any) {
		const name = <p>{`${row.original.userName}`}</p>;
		if (row.original.isPrimaryOrg === true) {
			return (
				<div style={{ display: "flex" }}>
					<Tooltip title="User">
						<Icon icon={User} style={{ marginTop: 3 }} />
					</Tooltip>
					{name}
				</div>
			);
		} else if (row.original.isPrimaryOrg === false) {
			return (
				<div style={{ display: "flex" }}>
					<Tooltip title="External">
						<Icon
							icon={UserLock}
							style={{ marginTop: 3 }}
							color="#FF5C39"
						/>
					</Tooltip>
					{name}
				</div>
			);
		} else {
			return <></>;
		}
	}

	private validateCurrentUser = (currentUser: DatasetUserDTO) => {
		return {
			user: {
				isValid: currentUser.userId !== undefined,
			},
			role: {
				isValid: currentUser.roleId !== undefined,
			},
		};
	};

	private isValid(validation: any) {
		return validation.user.isValid && validation.role.isValid;
	}

	private onUserChange = (value: any) => {
		const currentUser = { ...this.state.currentUser };
		currentUser.userId = value;
		this.setState({ currentUser });
	};

	private onRoleChange = (value: any) => {
		const currentUser = { ...this.state.currentUser };
		currentUser.roleId = value;
		this.setState({ currentUser });
	};

	private isIdInCollection(
		userId: number | undefined,
		userCollection: DatasetUserDTO[]
	) {
		if (userId === undefined) {
			return false;
		}
		const found =
			userCollection.find((usr) => usr.userId === userId) !== undefined;
		return found;
	}

	private fetchDatasetUsersAsync = () => {
		return DatasetAPI.getOrganizationDatasetUsers(
			this.state.organizationId,
			this.state.datasetId
		).then((datasetUsers: DatasetUserDTO[]) => {
			this.setState({ datasetUsers });
		});
	};

	private fetchOrganizationUsersAsync = () => {
		return OrganizationAPI.getOrganizationActiveUsers({
			organizationId: this.state.organizationId,
		}).then((organizationUsers: CurrentUserDTO[]) => {
			this.fullOrganizationUserList = organizationUsers;
		});
	};

	private fetchRolesAsync = () => {
		return OrganizationAPI.getOrganizationRoles({
			id: this.state.organizationId,
		}).then((roles) => {
			const datasetRoleTypeId = this.context.pickListItems.find(
				(p: PickListItemResponseDTO) => p.code === "DatasetRoleType"
			).id;
			const datasetRoles = roles.filter(
				(r) => r.roleTypeId === datasetRoleTypeId
			);
			this.roleList = datasetRoles;
		});
	};

	private filterUserListAndRedraw = () => {
		const organizationUsers = this.fullOrganizationUserList.filter(
			(usr) => !this.isIdInCollection(usr.id, this.state.datasetUsers)
		);
		this.setState({ organizationUsers });
	};

	private loadData = () => {
		this.setState({ loading: true });
		this.fetchDatasetUsersAsync()
			.then(() => this.fetchRolesAsync())
			.then(() => this.fetchOrganizationUsersAsync())
			.then(() => this.filterUserListAndRedraw())
			.then(() => this.setState({ loading: false, isLoading: false }));
	};

	private onSubmit = () => {
		const { datasetId } = this.props.match.params;
		this.setState({ loading: true });
		DatasetAPI.postOrganizationDatasetUser(
			this.state.organizationId,
			this.state.currentUser
		)
			.then(() => {
				this.setState({ currentUser: { datasetId } });
			})
			.then(() => {
				toast.success("Assigned dataset accessibility to selected user.");
				this.loadData();
			})
			.catch((e) => {
				toast.error("Something went wrong");
				this.setState({ loading: false, currentUser: { datasetId } });
			});
	};

	private deleteUserAsync(userId: number, roleId: number) {
		return DatasetAPI.deleteOrganizationDatasetUser({
			organizationId: this.state.organizationId,
			datasetId: this.state.datasetId,
			userId,
			roleId,
		});
	}

	private deleteUser(userId: number, roleId: number) {
		this.setState({ loading: true });
		this.deleteUserAsync(userId, roleId)
			.then(() => this.fetchDatasetUsersAsync())
			.then(() => this.filterUserListAndRedraw())
			.then(() => {
				toast.success("Removed dataset accessibility from selected user.");
				this.setState({ loading: false });
			})
			.catch((e) => {
				toast.error("Something went wrong");
				this.setState({ loading: false });
			});
	}

	private getRoleName(roleId: number) {
		const value = this.roleList.find((i) => i.id === roleId);
		if (value === undefined) {
			return "Undefined";
		}
		return value.name;
	}
}

const OrganizationDatasetsUsersForm = Form.create()(
	OrganizationDatasetsUsers as any
);
export default OrganizationDatasetsUsersForm;
